// Shared document-head helpers. The app is a client-rendered SPA, so every
// page writes its own title/meta on mount and restores it on unmount.

export const SITE_NAME = 'Nish Global';

/** The four locales the site ships copy for. Used for hreflang. */
export const LOCALES = ['en', 'tr', 'ru', 'ar'] as const;

export const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export const setCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
};

/**
 * Publishes hreflang alternates for the current path. The site serves all
 * languages from one URL, so each alternate points at the same path with a
 * ?lang= hint — enough for crawlers to see the alternates exist.
 */
export const setHreflang = (path: string) => {
  document.head
    .querySelectorAll('link[rel="alternate"][data-seo="hreflang"]')
    .forEach((el) => el.remove());
  if (typeof window === 'undefined') return;
  const { origin } = window.location;
  [...LOCALES, 'x-default'].forEach((loc) => {
    const el = document.createElement('link');
    el.rel = 'alternate';
    el.setAttribute('hreflang', loc);
    el.setAttribute('data-seo', 'hreflang');
    el.href = loc === 'x-default' ? `${origin}${path}` : `${origin}${path}?lang=${loc}`;
    document.head.appendChild(el);
  });
};

/** Keeps <html lang> in step with the active i18n language. */
export const setHtmlLang = (lang: string) => {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
};

export const setJsonLd = (id: string, data: unknown) => {
  document.getElementById(id)?.remove();
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = id;
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
};

export const removeJsonLd = (id: string) => {
  document.getElementById(id)?.remove();
};

export const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Title, description, canonical, OG and Twitter in one call. */
export const applyPageSeo = (opts: {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
}) => {
  const { title, description, path, type = 'website', image } = opts;
  const full = `${title} | ${SITE_NAME}`;
  document.title = full;
  setMeta('name', 'description', description);
  setMeta('property', 'og:title', full);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', full);
  setMeta('name', 'twitter:description', description);
  if (typeof window !== 'undefined') {
    const url = `${window.location.origin}${path}`;
    setMeta('property', 'og:url', url);
    setCanonical(url);
    if (image) setMeta('property', 'og:image', `${window.location.origin}${image}`);
  }
  setHreflang(path);
};
